import React, { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, Loader2, Pin, Mail, MailX, Check, MessagesSquare } from "lucide-react";
import { useAuth, useAuthCallback } from "../../lib/auth";
import {
  fetchMyPins,
  fetchMySubscription,
  cancelMySubscription,
  listSessions,
} from "../../lib/api";

export default function UserMenu({ onOpenPinned }) {
  const { user, loading, signIn, signOut } = useAuth();
  const cb = useAuthCallback();
  const [open, setOpen] = useState(false);
  const [pinCount, setPinCount] = useState(null);
  const [sub, setSub] = useState(null); // {subscribed, email} | null
  const [sessionCount, setSessionCount] = useState(null);
  const [unsubLoading, setUnsubLoading] = useState(false);

  const refreshMeta = useCallback(async () => {
    if (!user) return;
    try {
      const [pins, s, sessions] = await Promise.all([
        fetchMyPins(),
        fetchMySubscription(),
        listSessions(),
      ]);
      setPinCount(pins.count);
      setSub(s);
      setSessionCount(Array.isArray(sessions) ? sessions.length : 0);
    } catch (e) {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    if (open && user) refreshMeta();
  }, [open, user, refreshMeta]);

  const handleViewPins = () => {
    setOpen(false);
    onOpenPinned?.();
  };

  const handleUnsubscribe = async () => {
    setUnsubLoading(true);
    try {
      await cancelMySubscription();
      setSub({ subscribed: false, email: null });
    } finally {
      setUnsubLoading(false);
    }
  };

  if (cb.processing || loading) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
        Signing in…
      </span>
    );
  }

  if (!user) {
    return (
      <button
        data-testid="signin-google-btn"
        onClick={signIn}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 font-mono text-[10px] uppercase tracking-widest text-zinc-300 transition-colors"
      >
        <LogIn className="h-3.5 w-3.5" />
        Sign in with Google
      </button>
    );
  }

  const initials = (user.name || user.email || "?").charAt(0).toUpperCase();

  return (
    <div className="relative" data-testid="user-menu">
      <button
        data-testid="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 transition-colors"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-5 h-5 rounded-full border border-zinc-800"
          />
        ) : (
          <span className="w-5 h-5 rounded-full bg-amber-500 text-zinc-950 flex items-center justify-center font-mono text-[10px] font-bold">
            {initials}
          </span>
        )}
        <span className="hidden md:inline font-mono text-[10px] uppercase tracking-widest text-zinc-300 max-w-[120px] truncate">
          {user.name || user.email}
        </span>
      </button>
      {open && (
        <div
          data-testid="user-menu-dropdown"
          className="absolute right-0 top-full mt-1 w-72 border border-zinc-800 bg-zinc-950 z-30 py-1 shadow-2xl"
        >
          <div className="px-3 py-2 border-b border-zinc-800">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Signed in as
            </div>
            <div className="text-xs text-zinc-200 truncate" title={user.email}>{user.email}</div>
          </div>

          <button
            data-testid="user-menu-pins"
            onClick={handleViewPins}
            className="w-full text-left px-3 py-2 hover:bg-zinc-900 flex items-center gap-2 border-b border-zinc-900"
          >
            <Pin className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
            <span className="flex-1">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                My pinned files
              </span>
              <span className="block text-[11px] text-zinc-500">
                {pinCount === null
                  ? "Loading…"
                  : pinCount === 0
                  ? "No pins yet — pin files in the archive"
                  : `${pinCount} pinned · synced across devices`}
              </span>
            </span>
            {pinCount !== null && pinCount > 0 && (
              <span
                data-testid="user-menu-pin-count"
                className="font-mono text-[10px] text-amber-400 tabular-nums"
              >
                {String(pinCount).padStart(2, "0")}
              </span>
            )}
          </button>

          <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2">
            {sub?.subscribed ? (
              <Mail className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
            ) : (
              <MailX className="h-3.5 w-3.5 text-zinc-600 shrink-0" />
            )}
            <span className="flex-1 min-w-0">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                Tranche alerts
              </span>
              <span
                data-testid="user-menu-sub-status"
                className="block text-[11px] text-zinc-500 truncate"
              >
                {sub === null
                  ? "Loading…"
                  : sub.subscribed
                  ? <>Subscribed <Check className="inline h-2.5 w-2.5 text-emerald-400 -translate-y-0.5" /></>
                  : "Not subscribed"}
              </span>
            </span>
            {sub?.subscribed && (
              <button
                data-testid="user-menu-unsubscribe"
                onClick={handleUnsubscribe}
                disabled={unsubLoading}
                className="font-mono text-[9px] uppercase tracking-widest text-zinc-400 hover:text-red-400 border border-zinc-800 hover:border-red-400/60 px-2 py-1 disabled:opacity-50"
              >
                {unsubLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Off"}
              </button>
            )}
          </div>

          <div
            data-testid="user-menu-sessions"
            className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2"
          >
            <MessagesSquare className="h-3.5 w-3.5 text-sky-400 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                Analyst sessions
              </span>
              <span className="block text-[11px] text-zinc-500">
                {sessionCount === null
                  ? "Loading…"
                  : sessionCount === 0
                  ? "No conversations yet"
                  : `${sessionCount} saved · history synced`}
              </span>
            </span>
            {sessionCount !== null && sessionCount > 0 && (
              <span
                data-testid="user-menu-session-count"
                className="font-mono text-[10px] text-sky-400 tabular-nums"
              >
                {String(sessionCount).padStart(2, "0")}
              </span>
            )}
          </div>

          <button
            data-testid="signout-btn"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            className="w-full text-left px-3 py-2 hover:bg-zinc-900 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-amber-400 flex items-center gap-2"
          >
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
